import React, { useEffect, useMemo, useState } from "react";
import SEO, { brandText } from "../components/SEO";
import { Link, useParams } from "react-router-dom";
import api from "../api";
import { BRAND } from "../config/brand";

const BASE_URL = BRAND.url;
const SITE_NAME = BRAND.name;

function TrendList() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/api/seo-trends")
      .then((res) => setPages(res.data.pages || []))
      .catch(() => setPages([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO title={`복합기·프린터 선택 가이드 | ${SITE_NAME}`} description={`${SITE_NAME}에서 복합기와 프린터의 선택 기준을 확인하세요.`} path="/trend" />
      <div className="min-h-screen bg-gray-50">
        <section className="relative overflow-hidden bg-slate-950 text-white">
          <img src="/videos/store-banner-poster.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
          <div className="relative max-w-6xl mx-auto px-4 py-20">
            <p className="text-sm font-semibold text-blue-300">검색 트렌드</p>
            <h1 className="mt-3 text-4xl md:text-5xl font-extrabold tracking-tight">복사기·복합기 구매 가이드</h1>
            <p className="mt-4 max-w-2xl text-white/75">렌탈 가격, 판매 상품, 토너 소모품, 사무실 복합기 선택 기준을 키워드별로 정리했습니다.</p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-12">
          {loading ? (
            <div className="text-center text-gray-400 py-12">불러오는 중...</div>
          ) : pages.length === 0 ? (
            <div className="text-center text-gray-400 py-12">준비된 트렌드 페이지가 없습니다.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pages.map((page) => (
                <Link key={page.id} to={`/trend/${page.slug}`} className="block bg-white border border-gray-200 rounded-lg p-5 hover:border-blue-300 hover:shadow-sm transition">
                  <span className="inline-flex px-2 py-1 rounded bg-blue-50 text-blue-700 text-xs font-semibold">{page.category}</span>
                  <h2 className="mt-3 font-bold text-gray-900 leading-snug">{brandText(page.title)}</h2>
                  <p className="mt-2 text-sm text-gray-500 line-clamp-3">{brandText(page.description)}</p>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export default function TrendSeoPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(Boolean(slug));
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    api.get(`/api/seo-trends/${encodeURIComponent(slug)}`)
      .then((res) => setPage(res.data.page))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const content = page?.content_json || {};
  const canonical = page ? `${BASE_URL}/trend/${page.slug}` : `${BASE_URL}/trend`;
  const articleJsonLd = useMemo(() => {
    if (!page) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: brandText(page.title),
      description: brandText(page.description),
      datePublished: page.published_at || page.created_at,
      dateModified: page.updated_at,
      inLanguage: "ko-KR",
      mainEntityOfPage: canonical,
      author: { "@type": "Organization", name: SITE_NAME },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: { "@type": "ImageObject", url: `${BASE_URL}/logo512.png` },
      },
    };
  }, [canonical, page]);


  const breadcrumbJsonLd = page && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "홈", item: BASE_URL },
      { "@type": "ListItem", position: 2, name: "검색 트렌드", item: `${BASE_URL}/trend` },
      { "@type": "ListItem", position: 3, name: page.keyword, item: canonical },
    ],
  };

  if (!slug) return <TrendList />;

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-gray-400">불러오는 중...</div>;
  }

  if (notFound || !page) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <SEO title="페이지를 찾을 수 없습니다" path={`/trend/${slug}`} noindex />
        <h1 className="text-2xl font-bold text-gray-900">페이지를 찾을 수 없습니다</h1>
        <Link to="/trend" className="mt-4 text-blue-600 hover:text-blue-800">트렌드 목록으로 이동</Link>
      </div>
    );
  }

  return (
    <>
      <SEO title={page.title} description={page.description} path={`/trend/${page.slug}`}>
        {articleJsonLd && <script type="application/ld+json">{JSON.stringify(articleJsonLd).replace(/</g, "\\u003c")}</script>}
        {breadcrumbJsonLd && <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd).replace(/</g, "\\u003c")}</script>}
      </SEO>

      <article className="bg-white">
        <section className="relative overflow-hidden bg-slate-950 text-white">
          <img src="/videos/store-banner-poster.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
          <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-24">
            <nav className="text-sm text-white/60 mb-8">
              <Link to="/" className="hover:text-white">홈</Link>
              <span className="mx-2">/</span>
              <Link to="/trend" className="hover:text-white">검색 트렌드</Link>
            </nav>
            <p className="text-sm font-semibold text-blue-300">{page.category}</p>
            <h1 className="mt-3 text-4xl md:text-6xl font-extrabold tracking-tight leading-tight">{brandText(page.hero_title || page.title)}</h1>
            <p className="mt-6 max-w-3xl text-lg text-white/75">{brandText(page.intro)}</p>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_260px] gap-10">
            <main className="space-y-10">
              {(content.sections || []).map((section, index) => (
                <section key={index} className="border-b border-gray-100 pb-10 last:border-b-0">
                  <h2 className="text-2xl font-bold text-gray-900">{section.heading}</h2>
                  <p className="mt-4 text-gray-600 leading-8">{section.body}</p>
                  <ul className="mt-5 grid sm:grid-cols-2 gap-3">
                    {(section.bullets || []).map((item) => (
                      <li key={item} className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-3 text-sm text-gray-700">{item}</li>
                    ))}
                  </ul>
                </section>
              ))}

              {!!content.faqs?.length && (
                <section>
                  <p className="text-sm font-semibold text-blue-600">FAQ</p>
                  <h2 className="mt-2 text-2xl font-bold text-gray-900">자주 묻는 질문</h2>
                  <div className="mt-5 space-y-3">
                    {content.faqs.map((faq) => (
                      <details key={faq.q} className="rounded-lg border border-gray-200 p-4">
                        <summary className="cursor-pointer font-semibold text-gray-900">{faq.q}</summary>
                        <p className="mt-3 text-sm leading-7 text-gray-600">{faq.a}</p>
                      </details>
                    ))}
                  </div>
                </section>
              )}
            </main>

            <aside className="lg:sticky lg:top-20 self-start">
              <div className="rounded-lg border border-gray-200 p-5">
                <h2 className="font-bold text-gray-900">관련 바로가기</h2>
                <div className="mt-4 space-y-2">
                  {(content.links || []).map((link) => (
                    <Link key={link.path} to={link.path} className="block rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700">
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
              {!!content.related_keywords?.length && (
                <div className="mt-5 rounded-lg border border-gray-200 p-5">
                  <h2 className="font-bold text-gray-900">연관 검색어</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {content.related_keywords.map((keyword) => (
                      <span key={keyword} className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">{keyword}</span>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </article>
    </>
  );
}
